import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { pageVars } from '../../../pages/const/pageVars'

/**
 * The object is used to keep the operations on the grid
 * Could be Activation, Suspension, Deletion or mix of those
 */
let bulkUpdateContent = {
  activation: false,
  suspension: false,
  deletion: false
}

/**
 * Returns the operation on the Grid
 * Could be Activation, Suspension, Deletion or mix of those
 */
const getOperation = () => {
  const { activation, suspension, deletion } = bulkUpdateContent

  if (
    (activation && deletion) ||
    (suspension && deletion) ||
    (activation && suspension) ||
    (activation && suspension && deletion)
  )
    return 'success'

  if (activation) return 'activation'
  if (suspension) return 'suspension'
  if (deletion) return 'deletion'

  return 'success'
}

/**
 * Retrurns the appropriate push popup message
 */
const snackBarMessages = (type, pageName, flagged = false) => {
  const singularWording =
    !!pageName &&
    ((pageVars[pageName] && pageVars[pageName].singular) || 'record')
  const messageList = {
    success: {
      isOpen: false,
      message: 'Your changes were applied successfully',
      severity: 'success'
    },
    activation: {
      isOpen: false,
      message: flagged
        ? `Your have successfully flagged your ${singularWording}(s) for activation`
        : `Your have successfully activated your ${singularWording}(s)`,
      severity: 'activation'
    },
    suspension: {
      isOpen: false,
      message: flagged
        ? `Your have successfully flagged your ${singularWording}(s) for suspension`
        : `Your have successfully suspended your ${singularWording}(s)`,
      severity: 'suspension'
    },
    deletion: {
      isOpen: false,
      message: flagged
        ? `Your have successfully flagged your ${singularWording}(s) for deletion`
        : `Your have successfully deleted your ${singularWording}(s)`,
      severity: 'deletion'
    },
    deletionCancelled: {
      isOpen: false,
      message: `Your ${singularWording} is no longer marked for deletion`,
      severity: 'success'
    }
  }

  return messageList[type] || messageList['success']
}

const useGridWrapper = (
  entityTypeDisplayName,
  bulkPatchingCallback,
  showWarningAboutItemsItContains = false
) => {
  const navigate = useNavigate()
  const [isSnackbarOpen, setIsSnackbarOpen] = useState({
    ...snackBarMessages('success', entityTypeDisplayName),
    isOpen: false
  })
  const [editMode, setEditModePrivate] = useState(false)
  const [deletionChanges, setDeletionChanges] = useState({}) // uuid as key, {name, messageParts} as value
  const [activationChanges, setActivationChanges] = useState({}) // uuid as key, {checked, name, messageParts} as value
  const [isGridDirty, setIsGridDirty] = useState(false)
  const [loading, setLoading] = useState(false)
  const handSaveCallback = () => {
    if (showWarningAboutItemsItContains) return doBulkUpdates()

    if (showConfirmationDialog()) {
      doBulkUpdates()
    }
  }

  const showConfirmationDialog = () => {
    const activations = []
    const deactivations = []
    let warningRequired = false
    let finalMessage = ''

    for (const property in activationChanges) {
      const one = activationChanges[property]
      if (one.checked) {
        activations.push(one)
      } else {
        deactivations.push(one)
      }
    }

    if (activations.length) {
      finalMessage += `\nThe following ${entityTypeDisplayName}(s) are getting activated: \n`

      activations.forEach(a => {
        if (showWarningAboutItemsItContains) {
          finalMessage += `activating ${a.name} will also activate `
          a.messageParts.forEach(m => {
            if (m.value) {
              finalMessage += `${m.value} ${m.label
                .slice(0, -1)
                .toLowerCase()}(s), `
              warningRequired = true
            }
          })

          if (finalMessage.endsWith(' ')) finalMessage.slice(0, -2)
          finalMessage += '\n'
        } else {
          finalMessage += `${a.name} \n`
          warningRequired = true
        }
      })
    }
    if (deactivations.length) {
      finalMessage += `\nThe following ${entityTypeDisplayName.toLowerCase()}(s) are getting deactivated: \n`
      deactivations.forEach(d => {
        if (showWarningAboutItemsItContains) {
          finalMessage += `deactivating ${d.name} will also deactivate `

          d.messageParts.forEach(m => {
            if (m.value) {
              finalMessage += `${m.value} ${m.label
                .slice(0, -1)
                .toLowerCase()}(s), `
              warningRequired = true
            }
          })

          if (finalMessage.endsWith(' ')) finalMessage.slice(0, -2)
          finalMessage += '\n'
        } else {
          finalMessage += `${d.name} \n`
          warningRequired = true
        }

        if (finalMessage.endsWith(' ')) finalMessage.slice(0, -2)
        finalMessage += '\n'
      })
    }

    if (
      Object.keys(deletionChanges).length !== 0 ||
      Object.getPrototypeOf(deletionChanges) !== Object.prototype
    ) {
      finalMessage += `\nThe following ${entityTypeDisplayName}(s) are getting deleted: \n`

      for (const property in deletionChanges) {
        const one = deletionChanges[property]

        if (showWarningAboutItemsItContains) {
          finalMessage += `deleting ${one.name} will also activate `
          // eslint-disable-next-line
          one.messageParts.forEach(m => {
            if (m.value) {
              finalMessage += `${m.value} ${m.label
                .slice(0, -1)
                .toLowerCase()}(s), `
              warningRequired = true
            }
          })

          if (finalMessage.endsWith(' ')) finalMessage.slice(0, -2)
          finalMessage += '\n'
        } else {
          finalMessage += `${one.name} \n`
          warningRequired = true
        }

        if (finalMessage.endsWith(' ')) finalMessage.slice(0, -2)
        finalMessage += '\n'
      }
    }
    if (!warningRequired) finalMessage = ''
    finalMessage += '\n Are you sure you want to continue?'
    return window.confirm(finalMessage)
  }
  const doBulkUpdates = () => {
    setLoading(true);
    bulkUpdateContent = {
      activation: false,
      suspension: false,
      deletion: false
    }

    const bulkUpdateParameters = []
    for (const property in activationChanges) {
      const one = activationChanges[property]
      one.checked
        ? (bulkUpdateContent['activation'] = true)
        : (bulkUpdateContent['suspension'] = true)

      bulkUpdateParameters.push({
        id: property,
        suspended_at: one.checked ? null : Date.now()
      })
    }
    for (const property in deletionChanges) {
      bulkUpdateContent['deletion'] = true

      bulkUpdateParameters.push({
        id: property,
        deleted_at: Date.now()
      })
    }
    bulkPatchingCallback(bulkUpdateParameters)
      .unwrap()
      .then(() => {
        setIsSnackbarOpen({
          ...snackBarMessages(getOperation(), entityTypeDisplayName),
          isOpen: true
        })
        setTimeout(
          () => navigate(`/goto/${entityTypeDisplayName.toLowerCase()}`),
          1500
        )
      })
      .catch(e => alert(JSON.stringify(e)))
      .finally(() => setLoading(false))
  }
  const setEditMode = newValue => {
    if (!newValue) {
      setActivationChanges({})
      setDeletionChanges({})
      setIsGridDirty(false)
    }

    setEditModePrivate(newValue)
  }

  const deleteCallBack = (id, name, messageParts, setModalOpen) => {
    const ongoingDeletions = { ...deletionChanges }
    const isUnmarking = Object.prototype.hasOwnProperty.call(
      ongoingDeletions,
      id
    )

    if (isUnmarking) {
      delete ongoingDeletions[id]
    } else {
      ongoingDeletions[id] = { name, messageParts }
    }
    setDeletionChanges(ongoingDeletions)

    //deletion overrides deactivation warning
    const ongoingActivations = { ...activationChanges }
    if (!isUnmarking && ongoingActivations[id]) delete ongoingActivations[id]

    setActivationChanges(ongoingActivations)
    setIsGridDirty(
      Object.keys(ongoingDeletions).length !== 0 ||
        Object.keys(ongoingActivations).length !== 0
    )
    // Display pop up notificatin for deletion
    setIsSnackbarOpen({
      ...snackBarMessages(
        isUnmarking ? 'deletionCancelled' : 'deletion',
        entityTypeDisplayName,
        true
      ),
      isOpen: true
    })
    // Close Modal
    setModalOpen && setModalOpen(false)
  }
  const activationCallBack = (
    id,
    checked,
    name,
    messageParts,
    setModalOpen
  ) => {
    const operation = checked ? 'activation' : 'suspension'
    const ongoing = { ...activationChanges }

    if (ongoing[id]) {
      // the user can flip the switch back and forth, making sure only true changes are tracked
      delete ongoing[id]
    } else {
      ongoing[id] = { checked, name, messageParts }
    }

    setActivationChanges(ongoing)
    setIsGridDirty(
      Object.keys(deletionChanges).length !== 0 ||
        Object.getPrototypeOf(deletionChanges) !== Object.prototype ||
        Object.keys(ongoing).length !== 0 ||
        Object.getPrototypeOf(ongoing) !== Object.prototype
    )
    // Display pop up notificatin for activaton/suspension
    setIsSnackbarOpen({
      ...snackBarMessages(operation, entityTypeDisplayName, true),
      isOpen: true
    })
    // Close Modal
    setModalOpen && setModalOpen(false)
  }

  const detailCallBack = id => {
    if (entityTypeDisplayName.toLowerCase().includes('group'))
      navigate(`/groupDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('zone'))
      navigate(`/datazoneDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('entities'))
      navigate(`/resourceDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('accounts'))
      navigate(`/accountDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('services'))
      navigate(`/serviceDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('location'))
      navigate(`/locationDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('building'))
      navigate(`/buildingDetail/${id}`)

    if (entityTypeDisplayName.toLowerCase().includes('people'))
      navigate(`/peopleDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('users'))
      navigate(`/userDetail/${id}`)
    if (entityTypeDisplayName.toLowerCase().includes('skillsets'))
      navigate(`/skillsetDetail/${id}`)
  }

  const addCallBack = (type, subType) => {
    if (entityTypeDisplayName.toLowerCase().includes('group')) {
      navigate(`/groupCreate/${type}`)
    }
    if (entityTypeDisplayName.toLowerCase().includes('zone')) {
      if (subType) {
        navigate(`/datazoneCreate/${type}/${subType}`)
      } else {
        navigate(`/datazoneCreate/${type})`)
      }
    }

    if (entityTypeDisplayName.toLowerCase().includes('location')) {
      navigate(`/locationCreate`)
    }
    if (entityTypeDisplayName.toLowerCase().includes('building')) {
      navigate(`/buildingCreate/${'building'}`)
    }
    if (entityTypeDisplayName.toLowerCase().includes('entities')) {
      navigate(`/resourceCreate`)
    }
    if (entityTypeDisplayName.toLowerCase().includes('accounts')) {
      navigate(`/accountCreate`)
    }
    if (entityTypeDisplayName.toLowerCase().includes('services')) {
      navigate(`/serviceCreate`)
    }
    if (entityTypeDisplayName.toLowerCase().includes('people'))
      navigate(`/peopleCreate`)
    if (entityTypeDisplayName.toLowerCase().includes('users'))
      navigate(`/userCreate`)
    if (entityTypeDisplayName.toLowerCase().includes('skillsets'))
      navigate(`/skillsetCreate`)
  }
  return {
    editMode,
    setEditMode,
    isSnackbarOpen,
    setIsSnackbarOpen,
    deletionChanges,
    deleteCallBack,
    activationChanges,
    activationCallBack,
    handSaveCallback,
    detailCallBack,
    addCallBack,
    isGridDirty,
    loading
  }
}

export default useGridWrapper
